import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, IconButton, Tooltip, Badge } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { intl } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";
import { CURRENT_YEAR } from "./constants";

export const DmsAnalyticsHeader = ({
    title,
    children,
    actions,
    showRefresh = true,
    showFilters = true,
    sx,
    ...props
}) => {
    const { openFilters, refresh, filters, crossFilters } = useDmsAnalyticsContext();

    // Count active cross-filters (each type with at least one value)
    const crossFilterCount = Object.values(crossFilters || {}).filter(arr => arr && arr.length > 0).length;

    // Check if years filter is different from default (just current year)
    const isYearsFiltered = !(
        filters.years?.length === 1 && filters.years[0] === CURRENT_YEAR
    );

    const activeFilterCount = [
        filters.channel !== "all",
        isYearsFiltered,
        filters.startMonth !== 1 || filters.endMonth !== 12,
        crossFilterCount > 0,
    ].filter(Boolean).length;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                ...sx,
            }}
            {...props}
        >
            {/* Left: Title */}
            <Typography variant="h6" sx={{ color: "common.white", fontWeight: 600, flex: 1 }}>{title}</Typography>

            {/* Center: Children (e.g., metric toggle) */}
            {children && (
                <Box sx={{ display: "flex", justifyContent: "center", flex: 1 }}>
                    {children}
                </Box>
            )}

            {/* Right: Action buttons */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                {actions}
                {showRefresh && (
                    <Tooltip title={intl.get("DmsAnalytics.Action.Refresh").d("Atualizar")}>
                        <IconButton onClick={refresh} size="small" sx={{ color: "inherit" }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {showFilters && (
                    <Tooltip title={intl.get("DmsAnalytics.Action.Filters").d("Filtros")}>
                        <IconButton onClick={openFilters} size="small" sx={{ color: "inherit" }}>
                            <Badge badgeContent={activeFilterCount} color="error" invisible={activeFilterCount === 0}>
                                <FilterListIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

DmsAnalyticsHeader.propTypes = {
    title: PropTypes.node.isRequired,
    children: PropTypes.node,
    actions: PropTypes.node,
    showRefresh: PropTypes.bool,
    showFilters: PropTypes.bool,
    sx: PropTypes.object,
};
